import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    User,
    Mail,
    Calendar,
    Phone,
    Users,
    ChevronDown,
    ArrowLeft,
    Building2
} from 'lucide-react';
import flatpickr from 'flatpickr';
import { Hebrew } from 'flatpickr/dist/l10n/he';
import 'flatpickr/dist/flatpickr.min.css';

import Header from '../components/Header';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorDisplay from '../components/ErrorDisplay';
import { N8N_BASE_URL, API_ENDPOINTS } from '../utils/constants';
import { translateError } from '../utils/translations';

interface SurveyInfo {
    valid: boolean | string;
    organization_name?: string;
    survey_name?: string;
    error_description?: string;
}

const RegisterPage: React.FC = () => {
    const { instanceId } = useParams<{ instanceId: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [surveyInfo, setSurveyInfo] = useState<SurveyInfo | null>(null);

    const birthDateRef = useRef<HTMLInputElement>(null);
    const formRef = useRef<HTMLFormElement>(null);

    useEffect(() => {
        const fetchInfo = async () => {
            if (!instanceId) {
                // If instanceId is missing, show a specific error as requested
                setError('מזהה השאלון אינו תקין או שאינו קיים במערכת.');
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(`${N8N_BASE_URL}${API_ENDPOINTS.SURVEY_INFO}?instance_id=${instanceId}`);
                let data = await response.json();

                if (Array.isArray(data)) data = data[0];

                if (response.ok && data && (data.valid === true || data.valid === 'true')) {
                    setSurveyInfo(data);
                } else {
                    const msg = translateError(data?.error_description || 'השאלון אינו פעיל או שאינו קיים במערכת.');
                    setError(msg);
                }
            } catch (err) {
                console.error('Fetch error:', err);
                setError('נראה שיש בעיית תקשורת. אנא נסו לרענן את הדף.');
            } finally {
                setLoading(false);
            }
        };

        fetchInfo();
    }, [instanceId]);

    useEffect(() => {
        if (!loading && !error && birthDateRef.current) {
            const fp = flatpickr(birthDateRef.current, {
                locale: Hebrew,
                dateFormat: "Y-m-d",
                altInput: true,
                altFormat: "d/m/Y",
                maxDate: "today",
                minDate: "1940-01-01",
                disableMobile: true,
                monthSelectorType: "dropdown",
                disable: [
                    (date) => date > new Date()
                ],
                onReady: (_, __, instance) => {
                    // Helper to create menus only once and reuse them
                    const ensureMenusExist = () => {
                        let monthMenu = instance.calendarContainer.querySelector('.custom-month-dropdown-menu') as HTMLElement;
                        let yearMenu = instance.calendarContainer.querySelector('.custom-year-dropdown-menu') as HTMLElement;

                        if (!monthMenu) {
                            monthMenu = document.createElement("div");
                            monthMenu.className = "month-dropdown-menu custom-month-dropdown-menu"; // Added custom class for safer selection
                            instance.calendarContainer.appendChild(monthMenu);
                        }

                        if (!yearMenu) {
                            yearMenu = document.createElement("div");
                            yearMenu.className = "year-dropdown-menu custom-year-dropdown-menu";
                            instance.calendarContainer.appendChild(yearMenu);
                        }

                        return { monthMenu, yearMenu };
                    };

                    const injectCustomHeader = () => {
                        // Explicitly hide the original input to prevent duplication
                        instance.input.style.display = 'none';

                        const currentMonthContainer = instance.calendarContainer.querySelector('.flatpickr-current-month');
                        if (!currentMonthContainer) return;

                        // native inputs checks and hiding
                        const nativeMonthSelect = currentMonthContainer.querySelector('.flatpickr-monthDropdown-months') as HTMLElement;
                        const nativeYearInput = currentMonthContainer.querySelector('.numInputWrapper') as HTMLElement;
                        if (nativeMonthSelect) nativeMonthSelect.style.setProperty('display', 'none', 'important');
                        if (nativeYearInput) nativeYearInput.style.setProperty('display', 'none', 'important');

                        // Check if triggers already exist in this render cycle
                        if (currentMonthContainer.querySelector('.header-title-group')) return;

                        const { monthMenu, yearMenu } = ensureMenusExist();

                        const closeAllCustomMenus = () => {
                            monthMenu.classList.remove("show");
                            yearMenu.classList.remove("show");
                        };

                        // 1. Create CUSTOM MONTH Dropdown Trigger
                        const monthTrigger = document.createElement("div");
                        monthTrigger.className = "flatpickr-month-custom-trigger";

                        const updateTriggerMonthText = () => {
                            if (instance.l10n.months.longhand[instance.currentMonth]) {
                                monthTrigger.textContent = instance.l10n.months.longhand[instance.currentMonth];
                            }
                        };
                        updateTriggerMonthText();

                        // Populate Month Menu (Idempotent check not strictly needed if we rebuild content, but safer to rebuild to match state)
                        monthMenu.innerHTML = ''; // Clear current options to rebuild with correct active state
                        const updateActiveMonthAttrs = () => {
                            const now = new Date();
                            const isCurrentYear = instance.currentYear === now.getFullYear();
                            monthMenu.querySelectorAll(".dropdown-option").forEach((el, idx) => {
                                const isSelected = idx === instance.currentMonth;
                                el.classList.toggle("selected", isSelected);
                                if (isCurrentYear && idx > now.getMonth()) {
                                    el.classList.add("disabled");
                                } else {
                                    el.classList.remove("disabled");
                                }
                            });
                        };

                        instance.l10n.months.longhand.forEach((monthName, i) => {
                            const option = document.createElement("div");
                            option.className = "dropdown-option";
                            option.textContent = monthName;
                            option.onclick = (e) => {
                                e.stopPropagation();
                                if (option.classList.contains('disabled')) return;
                                instance.changeMonth(i, false);
                                updateTriggerMonthText();
                                closeAllCustomMenus();
                                updateActiveMonthAttrs();
                            };
                            monthMenu.appendChild(option);
                        });
                        updateActiveMonthAttrs();

                        monthTrigger.onclick = (e) => {
                            e.stopPropagation();
                            const wasOpen = monthMenu.classList.contains("show");
                            closeAllCustomMenus();
                            if (!wasOpen) {
                                const rect = monthTrigger.getBoundingClientRect();
                                const containerRect = instance.calendarContainer.getBoundingClientRect();
                                monthMenu.style.left = (rect.left - containerRect.left) + "px";
                                monthMenu.style.top = (rect.bottom - containerRect.top) + "px";
                                monthMenu.style.minWidth = monthTrigger.offsetWidth + "px";
                                monthMenu.classList.add("show");
                            }
                        };

                        // 2. Create CUSTOM YEAR Dropdown Trigger
                        const yearTrigger = document.createElement("div");
                        yearTrigger.className = "flatpickr-year-custom-trigger";
                        yearTrigger.textContent = instance.currentYear.toString();

                        // Populate Year Menu
                        yearMenu.innerHTML = '';
                        const updateActiveYearAttrs = () => {
                            yearMenu.querySelectorAll(".dropdown-option").forEach(el => {
                                el.classList.toggle("selected", parseInt(el.textContent || '0', 10) === instance.currentYear);
                            });
                        };

                        const currentYear = new Date().getFullYear();
                        for (let i = currentYear; i >= 1940; i--) {
                            const option = document.createElement("div");
                            option.className = "dropdown-option";
                            option.textContent = i.toString();
                            option.onclick = (e) => {
                                e.stopPropagation();
                                instance.changeYear(i);
                                yearTrigger.textContent = i.toString();
                                closeAllCustomMenus();
                                updateActiveYearAttrs();
                                // We don't need to re-inject manually here usually, observing events is better
                            };
                            yearMenu.appendChild(option);
                        }
                        updateActiveYearAttrs();

                        yearTrigger.onclick = (e) => {
                            e.stopPropagation();
                            const wasOpen = yearMenu.classList.contains("show");
                            closeAllCustomMenus();
                            if (!wasOpen) {
                                const rect = yearTrigger.getBoundingClientRect();
                                const containerRect = instance.calendarContainer.getBoundingClientRect();
                                yearMenu.style.left = (rect.left - containerRect.left) + "px";
                                yearMenu.style.top = (rect.bottom - containerRect.top) + "px";
                                yearMenu.style.minWidth = yearTrigger.offsetWidth + "px";
                                yearMenu.classList.add("show");
                            }
                        };

                        // Interaction Sync
                        document.addEventListener("click", closeAllCustomMenus);

                        const titleGroup = document.createElement("div");
                        titleGroup.className = "header-title-group";
                        titleGroup.appendChild(monthTrigger);
                        titleGroup.appendChild(yearTrigger);

                        currentMonthContainer.appendChild(titleGroup);

                        // Listeners updates
                        // We attach these once to instance, but we need to update the closured vars if we weren't careful.
                        // However, triggers are recreated, so we must update their text manually or rely on separate listeners
                        // that find the CURRENT triggers in DOM.
                        // Simpler: Just update the triggers we just created.
                        instance.set('onMonthChange', () => {
                            updateTriggerMonthText();
                            updateActiveMonthAttrs();
                        });
                        instance.set('onYearChange', () => {
                            yearTrigger.textContent = instance.currentYear.toString();
                            updateActiveYearAttrs();
                            updateActiveMonthAttrs();
                            // If Flatpickr redraws the header (destroying titleGroup), we need to re-inject.
                            // But usually onYearChange happens triggers internal redraw.
                            // Let's use a timeout to re-check if our header is gone.
                            setTimeout(injectCustomHeader, 0);
                        });
                    };

                    injectCustomHeader();
                }
            });

            return () => fp.destroy();
        }
    }, [loading, error]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formRef.current) return;

        setSubmitting(true);
        setSubmitError(null);

        const formData = new FormData(formRef.current);
        const payload = {
            ...Object.fromEntries(formData.entries()),
            instance_id: instanceId,
            date_of_birth: birthDateRef.current?.value // Ensure we get the raw value
        };

        try {
            const response = await fetch(`${N8N_BASE_URL}${API_ENDPOINTS.REGISTER}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const text = await response.text();
            let data: any = {};
            try {
                if (text) data = JSON.parse(text);
            } catch (jsonErr) {
                console.warn('Response was not valid JSON:', text);
                // If it's not JSON but response is OK, we treat it as success if no body is expected
                if (!response.ok) throw new Error('התקבלה תשובה לא תקינה מהשרת.');
            }

            if (response.ok && (!data || !data.error)) {
                navigate(`/session/${data.token}`);
                return;
            } else {
                const msg = translateError(data?.error_description || 'שגיאה בשליחת הטופס. אנא נסו שוב מאוחר יותר.');
                throw new Error(msg || 'Error');
            }
        } catch (err: any) {
            setSubmitError(err.message);
            // Clear error after 10s
            setTimeout(() => setSubmitError(null), 10000);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="min-h-screen bg-white flex flex-col"><Header /><main className="flex-grow flex flex-col items-center pt-20"><LoadingSpinner message="טוען פרטי שאלון..." /></main></div>;
    if (submitting) return <div className="min-h-screen bg-white flex flex-col"><Header /><main className="flex-grow flex flex-col items-center pt-20"><LoadingSpinner message="שולח נתונים..." /></main></div>;
    if (error) return <ErrorDisplay message={error} />;

    return (
        <div className="min-h-screen flex flex-col bg-white">
            <Header />

            <main className="flex-grow flex flex-col items-center px-6 pt-12 pb-20 relative z-20">
                <div className="max-w-2xl mx-auto text-center mb-10">
                    <h1 className="text-3xl sm:text-4xl font-bold mb-4 text-brand-dark">
                        {surveyInfo?.survey_name ? `הרשמה לשאלון: ${surveyInfo.survey_name}` : 'הרשמה לשאלון שרירי חוסן'}
                    </h1>
                    <p className="text-lg max-w-2xl mx-auto font-light leading-relaxed text-brand-dark/70">
                        אנא מלאו את הפרטים האישיים כדי שנוכל לשלוח לכם קישור אישי לתחילת השאלון.
                    </p>
                </div>

                <div className="w-full max-w-xl premium-card rounded-3xl p-8 sm:p-12">
                    <form ref={formRef} onSubmit={handleSubmit} className="space-y-8">
                        <div className="grid grid-cols-1 gap-8">

                            {/* Organization Info Banner */}
                            <div className="bg-brand-gold/5 border border-brand-gold/10 p-4 rounded-2xl flex items-center gap-4">
                                <div className="bg-white p-2 rounded-lg shadow-sm font-bold text-brand-gold">
                                    <Building2 className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-xs text-brand-gold font-bold uppercase tracking-wider">ארגון</p>
                                    <p className="font-bold text-brand-dark leading-tight">
                                        {surveyInfo?.organization_name || '-'}
                                    </p>
                                </div>
                            </div>

                            {/* Full Name */}
                            <div className="input-group">
                                <label className="block text-sm font-medium mb-2 text-brand-dark">שם מלא *</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-brand-dark/30">
                                        <User className="w-5 h-5" />
                                    </div>
                                    <input
                                        type="text"
                                        name="name"
                                        required
                                        className="w-full pr-12 pl-4 py-4 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-gold/20 focus:border-brand-gold transition-all"
                                        placeholder="שם מלא"
                                    />
                                </div>
                            </div>

                            {/* Email */}
                            <div className="input-group">
                                <label className="block text-sm font-medium mb-2 text-brand-dark">כתובת אימייל *</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-brand-dark/30">
                                        <Mail className="w-5 h-5" />
                                    </div>
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        className="w-full pr-12 pl-4 py-4 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-gold/20 focus:border-brand-gold transition-all"
                                        placeholder="mail@example.com"
                                    />
                                </div>
                                <p className="mt-2 text-xs text-brand-dark/50">הקישור האישי יישלח לכתובת זו.</p>
                            </div>

                            {/* Date of Birth */}
                            <div className="input-group">
                                <label className="block text-sm font-medium mb-2 text-brand-dark">תאריך לידה *</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-brand-dark/30">
                                        <Calendar className="w-5 h-5" />
                                    </div>
                                    <input
                                        ref={birthDateRef}
                                        type="text"
                                        name="date_of_birth"
                                        placeholder="תאריך לידה"
                                        required
                                        className="w-full pr-12 pl-4 py-4 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-gold/20 focus:border-brand-gold transition-all cursor-pointer"
                                    />
                                </div>
                            </div>

                            {/* Phone */}
                            <div className="input-group">
                                <label className="block text-sm font-medium mb-2 text-brand-dark">מספר טלפון</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-brand-dark/30">
                                        <Phone className="w-5 h-5" />
                                    </div>
                                    <input
                                        type="tel"
                                        name="phone"
                                        className="w-full pr-12 pl-4 py-4 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-gold/20 focus:border-brand-gold transition-all"
                                        placeholder="050-0000000"
                                    />
                                </div>
                            </div>

                            {/* Gender */}
                            <div className="input-group">
                                <label className="block text-sm font-medium mb-2 text-brand-dark">מגדר *</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-brand-dark/30">
                                        <Users className="w-5 h-5" />
                                    </div>
                                    <select
                                        name="gender"
                                        required
                                        className="w-full pr-12 pl-4 py-4 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-gold/20 focus:border-brand-gold appearance-none transition-all"
                                    >
                                        <option value="" disabled selected>בחרו מגדר</option>
                                        <option value="male">זכר</option>
                                        <option value="female">נקבה</option>
                                        <option value="other">אחר</option>
                                        <option value="prefer_not_to_say">מעדיף/ה לא לציין</option>
                                    </select>
                                    <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-brand-dark/30">
                                        <ChevronDown className="w-5 h-5" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="pt-4">
                            {submitError && (
                                <div className="mb-4 flex flex-col items-center gap-2 bg-red-50 border border-red-100 p-4 rounded-2xl text-red-800 text-sm text-center animate-in fade-in slide-in-from-top-2 duration-300">
                                    <div className="w-full">{submitError}</div>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full btn-premium py-5 px-8 rounded-2xl text-lg flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {submitting ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        מעבד...
                                    </>
                                ) : (
                                    <>
                                        שלחו לי קישור לשאלון
                                        <ArrowLeft className="w-5 h-5" />
                                    </>
                                )}
                            </button>
                            <p className="mt-6 text-center text-xs text-brand-dark/40">
                                בלחיצה על הכפתור, הנך מסכים/ה לתנאי השימוש ומדיניות הפרטיות שלנו.
                            </p>
                        </div>
                    </form>
                </div>
            </main>

            {/* Footer */}
            <footer className="py-12 border-t border-brand-gold/10 bg-white">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <p className="text-sm text-brand-dark/50 italic mb-4">"החוסן הוא השריר החשוב ביותר בארגון המודרני"</p>
                    <p className="text-xs text-brand-dark/30 font-light">&copy; 2026 קארן אבנם - ייעוץ ארגוני ופיתוח מנהיגות. כל הזכויות שמורות.</p>
                </div>
            </footer>
        </div>
    );
};

export default RegisterPage;
